import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import Modal from '../common/Modal';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency } from '../../utils/formatters';
import '../../styles/subscription.css';

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const { user, loadUser } = useAuth();
  const [modules, setModules] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [phone, setPhone] = useState('');
  const [stkLoading, setStkLoading] = useState(false);
  const [payStatus, setPayStatus] = useState('pending');
  const pollRef = useRef(null);

  useEffect(() => {
    loadModules();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const loadModules = async () => {
    setLoading(true);
    try {
      const res = await api.get('/subscription.php/modules');
      setModules(res.data.modules || []);
      setSelected((res.data.selected_module_ids || []).map(Number));
      setPaymentInfo(res.data.payment || null);
    } catch (error) {
      console.error('Failed to load modules:', error);
      toast.error('Failed to load apps');
    } finally {
      setLoading(false);
    }
  };

  const monthlyTotal = useMemo(() => {
    return modules
      .filter((m) => selected.includes(Number(m.id)))
      .reduce((sum, m) => sum + parseFloat(m.price_monthly || 0), 0);
  }, [modules, selected]);

  const toggleModule = (id) => {
    const numId = Number(id);
    setSelected((prev) =>
      prev.includes(numId) ? prev.filter((x) => x !== numId) : [...prev, numId]
    );
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = (paymentId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/subscription.php/payment-status/${paymentId}`);
        const status = res.data.payment?.status;
        setPayStatus(status || 'pending');
        if (status === 'completed') {
          stopPolling();
          toast.success('Payment received — apps unlocked');
          if (typeof loadUser === 'function') await loadUser();
          setShowPay(false);
          navigate('/settings/subscription');
        } else if (status === 'failed' || status === 'cancelled') {
          stopPolling();
          toast.error(res.data.payment?.result_desc || 'Payment failed');
        }
      } catch (err) {
        console.error(err);
      }
    }, 4000);
  };

  const handleSave = async () => {
    if (selected.length === 0) {
      toast.error('Select at least one app');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/subscription.php/select-modules', {
        module_ids: selected,
        phone: phone || undefined
      });

      if (res.data.payment_required) {
        setPaymentInfo({
          ...(res.data.payment || {}),
          instructions: res.data.instructions,
          enabled: true
        });
        setPayStatus('pending');
        setShowPay(true);
        if (res.data.payment?.id) {
          startPolling(res.data.payment.id);
        }
        toast.success('Complete M-Pesa payment to activate apps');
      } else {
        toast.success(
          `Apps saved — ${formatCurrency(res.data.monthly_total)} / month`
        );
        if (typeof loadUser === 'function') await loadUser();
        navigate('/settings/subscription');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save apps');
    } finally {
      setSaving(false);
    }
  };

  const handleStk = async () => {
    if (!phone) {
      toast.error('Enter the M-Pesa phone number');
      return;
    }
    if (!paymentInfo?.id) {
      toast.error('No pending payment');
      return;
    }
    setStkLoading(true);
    try {
      const res = await api.post('/subscription.php/stk-pay', {
        payment_id: paymentInfo.id,
        phone
      });
      toast.success(res.data.message || 'Check your phone for the M-Pesa prompt');
      startPolling(paymentInfo.id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'STK Push failed');
    } finally {
      setStkLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading apps…</div>;
  }

  return (
    <div className="subscription-plans module-picker">
      <div className="plans-header">
        <div>
          <h1>Choose Your Apps</h1>
          <p className="plans-subtitle">
            Pick the Harvyq ERP modules you need. Your monthly cost is the sum of selected apps.
            {paymentInfo?.enabled
              ? ' Payment via M-Pesa Paybill unlocks access after a successful transaction.'
              : ''}
          </p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/settings/subscription')}>
          Back
        </Button>
      </div>

      <div className="module-cost-bar">
        <div>
          <span className="cost-label">Monthly total</span>
          <span className="cost-amount">{formatCurrency(monthlyTotal)}</span>
        </div>
        <span className="cost-count">
          {selected.length} app{selected.length === 1 ? '' : 's'} selected
          {user?.business_name ? ` · ${user.business_name}` : ''}
        </span>
      </div>

      <div className="plans-grid modules-grid">
        {modules.map((mod) => {
          const isSelected = selected.includes(Number(mod.id));
          return (
            <Card
              key={mod.id}
              className={`plan-card module-card ${isSelected ? 'selected' : ''}`}
              onClick={() => toggleModule(mod.id)}
            >
              <div className="module-card-top">
                <span className="module-icon">{mod.icon}</span>
                <label className="module-check" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleModule(mod.id)}
                  />
                </label>
              </div>
              <h3>{mod.module_name}</h3>
              <p className="module-desc">{mod.description}</p>
              <div className="plan-price">
                <span className="amount">{formatCurrency(mod.price_monthly)}</span>
                <span className="period">/month</span>
              </div>
              <Button
                variant={isSelected ? 'primary' : 'secondary'}
                fullWidth
                onClick={(e) => {
                  e.stopPropagation();
                  toggleModule(mod.id);
                }}
              >
                {isSelected ? 'Selected' : 'Add app'}
              </Button>
            </Card>
          );
        })}
      </div>

      <div className="module-save-bar">
        <div>
          <strong>Total due monthly:</strong> {formatCurrency(monthlyTotal)}
        </div>
        <Button variant="primary" loading={saving} onClick={handleSave}>
          {paymentInfo?.enabled && paymentInfo?.require_payment !== false
            ? 'Continue to M-Pesa'
            : 'Save selection'}
        </Button>
      </div>

      <Modal
        isOpen={showPay}
        onClose={() => {
          stopPolling();
          setShowPay(false);
        }}
        title="Pay with M-Pesa"
      >
        <div className="mpesa-pay-modal">
          <p>
            Amount due: <strong>{formatCurrency(paymentInfo?.amount || monthlyTotal)}</strong>
          </p>
          <p className="pay-status">
            Status: <strong>{payStatus}</strong>
            {payStatus === 'pending' ? ' — waiting for payment…' : ''}
          </p>

          <Card title="Option 1 — STK Push (recommended)">
            <p>We send a payment prompt to your phone. Enter PIN to approve.</p>
            <Input
              label="M-Pesa phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="07XXXXXXXX"
            />
            <Button variant="primary" loading={stkLoading} onClick={handleStk}>
              Send STK Push
            </Button>
          </Card>

          <Card title="Option 2 — Paybill">
            <ol className="paybill-steps">
              <li>Lipa na M-Pesa → Pay Bill</li>
              <li>
                Business number: <strong>{paymentInfo?.paybill_number || '—'}</strong>
              </li>
              <li>
                Account number: <strong>{paymentInfo?.account_reference || '—'}</strong>
              </li>
              <li>
                Amount: <strong>{formatCurrency(paymentInfo?.amount || monthlyTotal)}</strong>
              </li>
              <li>Enter PIN and wait — apps unlock automatically when confirmed</li>
            </ol>
            <p className="help-text">
              {paymentInfo?.instructions?.paybill ||
                'Keep this window open. We check payment status every few seconds.'}
            </p>
          </Card>
        </div>
      </Modal>
    </div>
  );
};

export default SubscriptionPlans;
